/**
 * StepperControl
 * Multi-step wizard layout.  Children are shown one step at a time.
 * Exposes Back/Next navigation and calls onComplete when the last step is submitted.
 */

import React, { useState, memo } from 'react';
import type { FormControl } from '@atlas-forms/types-js';

export interface StepperControlProps {
  control: FormControl & { children?: FormControl[] };
  renderChild: (child: FormControl) => React.ReactNode;
  onComplete?: () => void;
}

export const StepperControl = memo(function StepperControl({ control, renderChild, onComplete }: StepperControlProps) {
  const steps = control.children ?? [];
  const [currentStep, setCurrentStep] = useState(0);

  if (steps.length === 0) return null;

  const isFirst = currentStep === 0;
  const isLast = currentStep === steps.length - 1;

  return (
    <div className="stepper-control" data-control-id={control.id}>
      <div className="stepper-control__progress" aria-label="Step progress">
        {steps.map((step, i) => (
          <div
            key={step.id ?? i}
            className={`stepper-control__step ${i === currentStep ? 'stepper-control__step--active' : ''} ${i < currentStep ? 'stepper-control__step--done' : ''}`}
            aria-current={i === currentStep ? 'step' : undefined}
          >
            <span className="stepper-control__step-number">{i + 1}</span>
            <span className="stepper-control__step-label">{step.label ?? `Step ${i + 1}`}</span>
          </div>
        ))}
      </div>

      <div className="stepper-control__content">
        {renderChild(steps[currentStep])}
      </div>

      <div className="stepper-control__navigation">
        {!isFirst && (
          <button type="button" onClick={() => setCurrentStep((s) => s - 1)} className="stepper-control__back">
            Back
          </button>
        )}
        {!isLast ? (
          <button type="button" onClick={() => setCurrentStep((s) => s + 1)} className="stepper-control__next">
            Next
          </button>
        ) : (
          <button type="button" onClick={() => onComplete?.()} className="stepper-control__finish">
            Finish
          </button>
        )}
      </div>
    </div>
  );
});
